/**
 * Nummernkreise und der Beispielkunde.
 *
 * Beides steht hier zusammen, weil es dieselbe Frage betrifft: welche Zahl
 * bekommt der nächste Vorgang. Der Beispielkunde ist die Ausnahme davon —
 * er zählt nirgends mit —, und wer ihn loswerden will, sucht ihn genau hier.
 *
 * Der wichtigste Punkt auf dieser Seite ist die Startnummer. Wer aus einem
 * alten System kommt und dort bei Rechnung 183 stand, muss bei 184
 * weitermachen können. Zwei Rechnungen mit derselben Nummer sind nach
 * § 14 UStG kein Schönheitsfehler.
 *
 * Neben dem Startwert steht deshalb, was in diesem Jahr schon vergeben ist:
 * ein Startwert darunter bleibt wirkungslos, und das soll niemanden
 * überraschen.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  NUMMERNKREISHINWEIS,
  NUMMERNKREISTEXT,
  useBeispiel,
  useBeispielAnlegen,
  useBeispielEntfernen,
  useNummernkreisSetzen,
  useNummernkreise,
  type Nummernkreis,
} from '../api/kundenakte'
import { Fehlerhinweis, Ladeanzeige } from '../bausteine/Hinweise'

export function Nummernkreise() {
  const liste = useNummernkreise()

  return (
    <div className="space-y-4">
      <Beispielkarte />

      <h2 className="ueberschrift-akzent mb-3 text-xl">Nummernkreise</h2>

      {liste.isLoading && <Ladeanzeige />}
      <Fehlerhinweis fehler={liste.error} />

      {liste.data?.map((kreis) => (
        <Kreiskarte key={kreis.art} kreis={kreis} />
      ))}
    </div>
  )
}

function Beispielkarte() {
  const stand = useBeispiel()
  const anlegen = useBeispielAnlegen()
  const entfernen = useBeispielEntfernen()
  const [sicher, setzeSicher] = useState(false)

  if (stand.isLoading) return null

  const da = stand.data?.vorhanden

  return (
    <section className="karte-akzent">
      <h2 className="text-xl font-bold">Beispielkunde</h2>

      {da ? (
        <>
          <p className="mt-1">
            <span className="font-semibold">{stand.data?.name}</span> ist angelegt:
            zwei Spielplätze, sechs Geräte, eine abgeschlossene Hauptuntersuchung,
            ein Vertrag und ein Angebot im Entwurf.
          </p>
          <p className="mt-1 text-base text-slate-700">
            Er zählt in keinem Nummernkreis mit. Beim Entfernen geht alles mit,
            was an ihm hängt — echte Kunden bleiben unberührt.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {stand.data?.kunde_id && (
              <Link to={`/kunden/${stand.data.kunde_id}`} className="knopf-zweit px-3">
                Ansehen
              </Link>
            )}
            {sicher ? (
              <>
                <button
                  type="button"
                  className="knopf-warnung px-3"
                  onClick={() => entfernen.mutate()}
                  disabled={entfernen.isPending}
                >
                  {entfernen.isPending ? 'Entfernt …' : 'Wirklich entfernen'}
                </button>
                <button
                  type="button"
                  className="knopf-still px-3"
                  onClick={() => setzeSicher(false)}
                >
                  Abbrechen
                </button>
              </>
            ) : (
              <button
                type="button"
                className="knopf-still px-3"
                onClick={() => setzeSicher(true)}
              >
                Entfernen
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="mt-1">
            Kein Beispielkunde angelegt. Er ist eine vollständige Akte zum
            Ausprobieren und verbraucht keine Nummer.
          </p>
          <button
            type="button"
            className="knopf-haupt mt-3"
            onClick={() => anlegen.mutate()}
            disabled={anlegen.isPending}
          >
            {anlegen.isPending ? 'Legt an …' : 'Beispielkunden anlegen'}
          </button>
        </>
      )}

      <Fehlerhinweis fehler={anlegen.error ?? entfernen.error} />
    </section>
  )
}

function Kreiskarte({ kreis }: { kreis: Nummernkreis }) {
  const setzen = useNummernkreisSetzen()
  const [offen, setzeOffen] = useState(false)
  const [felder, setzeFelder] = useState({
    praefix: kreis.praefix,
    mit_jahr: kreis.mit_jahr,
    stellen: String(kreis.stellen),
    startnummer: String(kreis.startnummer),
    trenner: kreis.trenner,
    automatisch: kreis.automatisch,
  })

  function setze<K extends keyof typeof felder>(name: K, wert: (typeof felder)[K]) {
    setzeFelder((alt) => ({ ...alt, [name]: wert }))
  }

  /* Vorschau im Formular, ohne den Server zu fragen. Dieselbe Regel wie
     dort: Teile mit Trenner verbinden, leere weglassen. */
  const jahr = new Date().getFullYear()
  const teile = [felder.praefix, felder.mit_jahr ? String(jahr) : ''].filter(Boolean)
  const stellen = Math.min(Math.max(Number(felder.stellen) || 1, 1), 8)
  const start = Math.max(Number(felder.startnummer) || 1, 1)
  const vorschau =
    (teile.length ? teile.join(felder.trenner) + felder.trenner : '') +
    String(start).padStart(stellen, '0')

  return (
    <section className="karte">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-bold">{NUMMERNKREISTEXT[kreis.art]}</h3>
          <p className="text-base text-slate-700">{NUMMERNKREISHINWEIS[kreis.art]}</p>

          <p className="mt-2">
            Nächste Nummer:{' '}
            <span className="marke bg-akzent-grund font-mono text-akzent-700">
              {kreis.beispiel}
            </span>
          </p>
          <p className="text-base text-slate-600">
            {kreis.vergeben
              ? `Höchste in ${jahr} vergebene: ${kreis.vergeben}`
              : `In ${jahr} noch keine vergeben`}
          </p>
          {kreis.art === 'kunde' && (
            <p className="mt-1 text-base text-slate-600">
              Automatische Vergabe: {kreis.automatisch ? 'eingeschaltet' : 'aus'}
            </p>
          )}
        </div>

        <button
          type="button"
          className="knopf-still px-3"
          onClick={() => setzeOffen((o) => !o)}
        >
          {offen ? 'Schließen' : 'Ändern'}
        </button>
      </div>

      {offen && (
        <form
          className="mt-4 space-y-3 border-t-2 border-slate-200 pt-4"
          onSubmit={(e) => {
            e.preventDefault()
            setzen.mutate(
              {
                art: kreis.art,
                praefix: felder.praefix,
                mit_jahr: felder.mit_jahr,
                stellen,
                startnummer: start,
                trenner: felder.trenner,
                automatisch: felder.automatisch,
              },
              { onSuccess: () => setzeOffen(false) },
            )
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label htmlFor={`praefix-${kreis.art}`}>Kürzel</label>
              <input
                id={`praefix-${kreis.art}`}
                value={felder.praefix}
                onChange={(e) => setze('praefix', e.target.value)}
                maxLength={10}
                placeholder="RE"
              />
            </div>
            <div>
              <label htmlFor={`trenner-${kreis.art}`}>Trenner</label>
              <input
                id={`trenner-${kreis.art}`}
                value={felder.trenner}
                onChange={(e) => setze('trenner', e.target.value)}
                maxLength={3}
              />
            </div>
            <div>
              <label htmlFor={`stellen-${kreis.art}`}>Stellen</label>
              <input
                id={`stellen-${kreis.art}`}
                type="number"
                min={1}
                max={8}
                value={felder.stellen}
                onChange={(e) => setze('stellen', e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-center gap-3 font-normal">
            <input
              type="checkbox"
              checked={felder.mit_jahr}
              onChange={(e) => setze('mit_jahr', e.target.checked)}
              className="h-6 w-6 shrink-0"
              style={{ minHeight: 'auto' }}
            />
            <span>Jahreszahl mit aufnehmen</span>
          </label>

          <div>
            <label htmlFor={`start-${kreis.art}`}>Startnummer</label>
            <input
              id={`start-${kreis.art}`}
              type="number"
              min={1}
              value={felder.startnummer}
              onChange={(e) => setze('startnummer', e.target.value)}
            />
            <p className="mt-1 text-base text-slate-600">
              Für den Übergang aus einem alten System: stand dort die letzte
              Rechnung auf 183, tragen Sie 184 ein. Ein Wert unterhalb der schon
              vergebenen Nummern bleibt wirkungslos — es würde sonst eine Nummer
              ein zweites Mal geben.
            </p>
          </div>

          {kreis.art === 'kunde' && (
            <label className="flex items-start gap-3 font-normal">
              <input
                type="checkbox"
                checked={felder.automatisch}
                onChange={(e) => setze('automatisch', e.target.checked)}
                className="mt-1 h-6 w-6 shrink-0"
                style={{ minHeight: 'auto' }}
              />
              <span>
                <span className="font-semibold">Kundennummern automatisch vergeben</span>
                <span className="block text-base text-slate-600">
                  Ohne Haken bleibt das Feld beim Kunden frei und wird von Hand
                  gefüllt — so war es bisher.
                </span>
              </span>
            </label>
          )}

          <p className="rounded-feld bg-slate-100 p-3">
            So sieht die nächste Nummer aus:{' '}
            <span className="font-mono font-semibold">{vorschau}</span>
          </p>

          <Fehlerhinweis fehler={setzen.error} />

          <div className="flex gap-2">
            <button type="submit" className="knopf-haupt" disabled={setzen.isPending}>
              {setzen.isPending ? 'Speichert …' : 'Übernehmen'}
            </button>
            <button type="button" className="knopf-still" onClick={() => setzeOffen(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
    </section>
  )
}
